import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function ProSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | expired | failed
  const [detail, setDetail] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { nav("/pro"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current++;
      try {
        const { data } = await api.get(`/pro/checkout/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.user) setUser(data.user);
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
      } catch (err) {
        console.debug("[pro-success] status poll retry", err?.response?.status || err);
      }
      if (attempts.current >= 8) { setStatus("failed"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, nav, setUser]);

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-lg mx-auto px-6 py-24 text-center">
        {status === "checking" && (
          <div data-testid="pro-success-checking">
            <Loader2 className="mx-auto text-[var(--accent)] animate-spin" size={48}/>
            <h1 className="font-display text-3xl text-white mt-6">Confirming payment…</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2 font-mono">Attempt {attempts.current} / 8</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="pro-success-paid">
            <CheckCircle2 className="mx-auto text-[var(--profit)]" size={64}/>
            <h1 className="font-display text-4xl text-white mt-6">You're Pro.</h1>
            <p className="text-[var(--text-secondary)] mt-3">
              Pro features are now unlocked on your account.
              {detail?.user?.pro_expires_at && (
                <> Expires <span className="font-mono text-white">{new Date(detail.user.pro_expires_at).toLocaleDateString()}</span>.</>
              )}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard" data-testid="pro-success-dashboard-btn" className="btn-primary">Go to dashboard</Link>
              <Link to="/builder" className="btn-outline">Start building</Link>
            </div>
          </div>
        )}
        {status === "expired" && (
          <div data-testid="pro-success-expired">
            <XCircle className="mx-auto text-[var(--loss)]" size={64}/>
            <h1 className="font-display text-3xl text-white mt-6">Session expired</h1>
            <p className="text-[var(--text-secondary)] mt-2">Please try again from the Pro page.</p>
            <Link to="/pro" className="btn-outline inline-block mt-6">Back to Pro</Link>
          </div>
        )}
        {status === "failed" && (
          <div data-testid="pro-success-failed">
            <XCircle className="mx-auto text-[var(--warning)]" size={64}/>
            <h1 className="font-display text-3xl text-white mt-6">Payment status unclear</h1>
            <p className="text-[var(--text-secondary)] mt-2">Check your email for confirmation. Your Pro status will update automatically once payment clears.</p>
            <Link to="/dashboard" className="btn-outline inline-block mt-6">Go to dashboard</Link>
          </div>
        )}
      </main>
    </div>
  );
}
